import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { diagnosticsClient, formatTimestamp, shortId } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { useServerGrid } from "@/lib/use-server-grid";

export const Route = createFileRoute("/diagnostics/devices")({
	component: DiagnosticsDevicesPage,
	validateSearch: (search: Record<string, unknown>) => ({
		create: search.create === "1" ? "1" : undefined,
	}),
});

interface DeviceRow {
	id: string;
	deviceId: string;
	employeeId: string;
	platform: string;
	hostname: string;
	rolloutRing: string;
	status: string;
	claimedAt: unknown;
	lastSeenAt: unknown;
	createdAt: unknown;
}

function DiagnosticsDevicesPage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([{ label: t("title"), href: "/diagnostics" }, { label: t("sidebar_devices") }]);
	const search = Route.useSearch();
	const { onGridReady, onStateChanged } = useGridLayout("diagnostics-devices");
	const [createOpen, setCreateOpen] = useState(search.create === "1");
	const { selectedRows, onSelectionChanged, ActionBar } =
		useGridSelection<DeviceRow>();

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<DeviceRow>({
		fetchRows: async () => {
			const resp = await diagnosticsClient.listDeviceEnrollments({});
			const rows = (resp.enrollments ?? []) as DeviceRow[];
			return {
				rows,
				totalCount: rows.length,
			};
		},
		colToField: {
			rolloutRing: "rollout_ring",
			employeeId: "employee_id",
			lastSeenAt: "last_seen_at",
			createdAt: "created_at",
		},
	});

	const columnDefs = useMemo<ColDef<DeviceRow>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "hostname",
				headerName: t("devices_col_device"),
				width: 200,
				cellRenderer: (p: { data: DeviceRow | undefined }) => {
					const d = p.data;
					if (!d) return "";
					const main = d.hostname || "—";
					const sub = shortId(d.deviceId);
					return (
						<div>
							<span className="text-xs font-medium">{main}</span><br/>
							<span className="font-mono" style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{sub}</span>
						</div>
					);
				},
			},
			{
				field: "platform",
				headerName: t("devices_col_platform"),
				width: 100,
			},
			{
				field: "status",
				headerName: t("devices_col_status"),
				width: 110,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const v = p.value ?? "";
					const color = v === "active" ? "hsl(var(--chart-2))" : v === "claimed" ? "hsl(var(--chart-4))" : "hsl(var(--muted-foreground))";
					return (
						<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`, color }}>
							{v}
						</span>
					);
				},
			},
			{
				field: "rolloutRing",
				headerName: t("devices_col_ring"),
				width: 100,
				cellRenderer: (p: { value: string }) => (
					<span style={{ display: "inline-block", padding: "1px 8px", borderRadius: 9999, fontSize: 11, lineHeight: "18px", border: "1px solid color-mix(in srgb, hsl(var(--muted-foreground)) 20%, transparent)", color: "hsl(var(--muted-foreground))" }}>
						{t(`enum_ring_${p.value}`, { defaultValue: p.value ?? "" })}
					</span>
				),
			},
			{
				field: "employeeId",
				headerName: t("devices_col_employee"),
				width: 110,
				cellClass: "font-mono",
				valueFormatter: (p) => shortId(p.value),
			},
			{
				field: "lastSeenAt",
				headerName: t("devices_col_last_seen"),
				width: 160,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
			{
				field: "createdAt",
				headerName: t("devices_col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => formatTimestamp(p.value),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("devices_status_count", { defaultValue: "устройств" })}`,
				align: "left" as const,
			},
			...(selectedRows.length > 0
				? [
						{
							id: "selection",
							content: t("rows_selected", {
								count: selectedRows.length,
								defaultValue: `${selectedRows.length} выбрано`,
							}),
							align: "right" as const,
						},
					]
				: []),
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("devices_create_token")}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact<DeviceRow>
						ref={gridRef}
						{...gridProps}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						localeText={AG_GRID_LOCALE_RU}
						rowSelection="multiple"
						onSelectionChanged={onSelectionChanged}
						onGridReady={onGridReady}
						onSortChanged={() => { onStateChanged(); refreshGrid(); }}
						onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
						onColumnMoved={onStateChanged}
						onColumnResized={onStateChanged}
						onColumnVisible={onStateChanged}
						suppressRowClickSelection
						animateRows={false}
						rowHeight={28}
						headerHeight={32}
						getRowId={(p) => p.data.id}
						overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("devices_empty_title", { defaultValue: "Нет подключённых устройств" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("devices_empty_auto", { defaultValue: "Устройства регистрируются автоматически при подключении агента" })}</p></div>`}
						overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("loading", { defaultValue: "Загрузка" })}...</span>`}
					/>
				</div>

			<ActionBar />
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent>
					<CreateTokenForm
						onSuccess={() => {
							refreshGrid();
							toast.success(t("toast_device_created"));
						}}
						onClose={() => setCreateOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}

function CreateTokenForm({
	onSuccess,
	onClose,
}: { onSuccess: () => void; onClose: () => void }) {
	const { t } = useTranslation("diagnostics");
	const [employeeId, setEmployeeId] = useState("");
	const [observedPartyId, setObservedPartyId] = useState("");
	const [rolloutRing, setRolloutRing] = useState("canary");
	const [platformHint, setPlatformHint] = useState("");
	const [note, setNote] = useState("");
	const [expiresHours, setExpiresHours] = useState("72");
	const [createdPlaintext, setCreatedPlaintext] = useState<string | null>(null);

	const mutation = useMutation({
		mutationFn: () =>
			diagnosticsClient.createEnrollmentToken({
				employeeId,
				observedPartyId,
				rolloutRing,
				platformHint,
				note,
				expiresInHours: Number.parseInt(expiresHours, 10) || 72,
			}),
		onSuccess: (data) => {
			setCreatedPlaintext(data.plaintext);
			onSuccess();
		},
		onError: (err: Error) =>
			toast.error(t("toast_error_generic"), { description: err.message }),
	});

	if (createdPlaintext) {
		return (
			<>
				<DialogHeader>
					<DialogTitle>{t("devices_token_created")}</DialogTitle>
					<DialogDescription>{t("devices_token_copy_hint")}</DialogDescription>
				</DialogHeader>
				<div className="py-4">
					<Input
						readOnly
						value={createdPlaintext}
						className="font-mono text-xs"
						onClick={(event) => (event.target as HTMLInputElement).select()}
					/>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						OK
					</Button>
				</DialogFooter>
			</>
		);
	}

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("devices_create_title")}</DialogTitle>
				<DialogDescription>{t("devices_create_description")}</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label>{t("devices_employee_id")}</Label>
					<Input
						placeholder="UUID"
						value={employeeId}
						onChange={(event) => setEmployeeId(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("devices_observed_party_id")}</Label>
					<Input
						placeholder="UUID"
						value={observedPartyId}
						onChange={(event) => setObservedPartyId(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("devices_rollout_ring")}</Label>
					<Select value={rolloutRing} onValueChange={setRolloutRing}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="canary">{t("enum_ring_canary")}</SelectItem>
							<SelectItem value="pilot">{t("enum_ring_pilot")}</SelectItem>
							<SelectItem value="broad">{t("enum_ring_broad")}</SelectItem>
							<SelectItem value="restricted">
								{t("enum_ring_restricted")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("devices_platform_hint")}</Label>
					<Input
						placeholder="macos / windows / linux"
						value={platformHint}
						onChange={(event) => setPlatformHint(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("devices_note")}</Label>
					<Input
						value={note}
						onChange={(event) => setNote(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("devices_expires_hours")}</Label>
					<Input
						type="number"
						value={expiresHours}
						onChange={(event) => setExpiresHours(event.target.value)}
					/>
				</div>
			</div>
			<DialogFooter>
				<Button variant="outline" onClick={onClose}>
					{t("cancel")}
				</Button>
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !employeeId}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("devices_creating")}
						</>
					) : (
						t("devices_create_token")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
